import React, { useRef, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import { Stars, Float } from '@react-three/drei';

function LostSatellite() {
  const ref = useRef(null);
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.getElapsedTime();
    ref.current.rotation.x = Math.sin(t * 0.4) * 0.3;
    ref.current.rotation.y = t * 0.15;
    ref.current.rotation.z = Math.cos(t * 0.3) * 0.2;
    ref.current.position.y = Math.sin(t * 0.5) * 0.3;
    ref.current.position.x = Math.cos(t * 0.3) * 0.4;
  });

  return (
    <group ref={ref} scale={1.3}>
      <mesh>
        <boxGeometry args={[0.5, 0.4, 0.5]} />
        <meshStandardMaterial color="#c8ced8" flatShading metalness={0.6} roughness={0.5} />
      </mesh>
      <mesh position={[0, 0.05, 0.251]}>
        <planeGeometry args={[0.45, 0.08]} />
        <meshStandardMaterial color="#ff5c5c" emissive="#ff5c5c" emissiveIntensity={0.8} />
      </mesh>
      <mesh position={[-0.85, 0, 0]} rotation={[0, 0, 0.15]}>
        <boxGeometry args={[0.8, 0.02, 0.45]} />
        <meshStandardMaterial color="#1a4060" flatShading metalness={0.8} />
      </mesh>
      <mesh position={[0.85, 0, 0]} rotation={[0, 0, -0.25]}>
        <boxGeometry args={[0.8, 0.02, 0.45]} />
        <meshStandardMaterial color="#1a4060" flatShading metalness={0.8} />
      </mesh>
      <mesh position={[0, 0.3, 0]} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[0.15, 0.1, 8, 1, true]} />
        <meshStandardMaterial color="#e0e4ea" flatShading />
      </mesh>
      <mesh position={[0, 0.42, 0]}>
        <sphereGeometry args={[0.04, 12, 12]} />
        <meshStandardMaterial color="#ff5c5c" emissive="#ff5c5c" emissiveIntensity={1.5} />
      </mesh>
    </group>
  );
}

export default function Error() {
  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative', background: '#00040f', overflow: 'hidden' }}>
      <Canvas
        camera={{ position: [0, 0, 4], fov: 55 }}
        style={{ position: 'absolute', inset: 0, zIndex: 0 }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.3} />
          <pointLight position={[3, 3, 3]} intensity={1} color="#5ce1e6" />
          <pointLight position={[-5, -2, -3]} intensity={0.6} color="#ff5c5c" />
          <Stars radius={60} depth={40} count={2000} factor={3} fade speed={0.5} />
          <Float speed={1} rotationIntensity={0.3} floatIntensity={0.4}>
            <LostSatellite />
          </Float>
        </Suspense>
      </Canvas>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
        style={{
          position: 'absolute', inset: 0, zIndex: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', pointerEvents: 'none',
        }}
      >
        <motion.h1
          animate={{ textShadow: ['0 0 20px rgba(92,225,230,0.3)', '0 0 50px rgba(92,225,230,0.7)', '0 0 20px rgba(92,225,230,0.3)'] }}
          transition={{ duration: 3, repeat: Infinity }}
          style={{
            color: '#5ce1e6',
            fontFamily: 'Orbitron, monospace',
            fontSize: 'clamp(6rem, 18vw, 14rem)',
            margin: 0, letterSpacing: -4, lineHeight: 0.9,
          }}
        >
          404
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.6 }}
          style={{
            color: 'rgba(240,248,255,0.85)',
            fontFamily: 'Inter, sans-serif',
            fontSize: '1.3rem', fontWeight: 300,
            marginTop: 8, letterSpacing: 1,
          }}
        >
          Satellite perdu dans le vide spatial
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0, duration: 0.6 }}
          style={{
            color: 'rgba(240,248,255,0.45)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 12, marginTop: 4, letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          signal · lost · trajectory · unknown
        </motion.p>

        <motion.a
          href="/"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.5 }}
          whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(92,225,230,0.5)' }}
          whileTap={{ scale: 0.98 }}
          style={{
            marginTop: 40, padding: '14px 36px',
            background: 'transparent',
            border: '1px solid rgba(92,225,230,0.6)',
            color: '#5ce1e6',
            textDecoration: 'none',
            fontFamily: 'Inter, sans-serif',
            fontSize: 14, letterSpacing: 2,
            textTransform: 'uppercase',
            borderRadius: 2,
            pointerEvents: 'auto',
            cursor: 'pointer',
          }}
        >
          ← Retour à l'orbite
        </motion.a>
      </motion.div>
    </div>
  );
}
